import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { useCardDraft } from "../common/useCardDraft";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import { Stepper } from "../controls";
import AddictionService from "./AddictionService";
import {
  ADDICTION_STATUS,
  MAX_DESCRIPTION_WORDS,
  addictionBand,
  countWords,
  clampWords,
  isZeroDuration,
  normalizeDuration,
} from "./data";
import { ADD_V2_REFRESH } from "./events";

/**
 * The "Update" sheet for one addiction — px-matched to Figma 3071:2446
 * (`Card · Typhoid fever`, 340 wide).
 *
 * This used to be AddictionModal: a centred overlay on a scrim. The board
 * draws it as a CARD — 1px border, `$card-shadow`, a chevron-up collapse in
 * the header, no backdrop of any kind — which is the editor-rail idiom every
 * other v3 section already uses. It now lives in the rail's column, driven by
 * the shared useSheetStack, so up to four sheets (one per addiction) can be
 * open at once and are saved one at a time.
 *
 * Fields, in board order:
 *   1. Status — Regularly / Occasionally / Never / Quit
 *   2. Status update date
 *   3. Started when   · how long ago
 *   4. Total interval · total time used
 *   5. Frequency
 *   6. Description (optional, 250-word cap)
 *
 * Rules (unchanged from the modal):
 *   • Status = "never" → the detail fields are disabled and greyed; the
 *     description stays editable.
 *   • Status = "quit" → total interval is required.
 *   • Frequency is required for any status other than "never".
 *   • Save is a no-op unless something actually changed.
 */

const todayISO = () => new Date().toISOString().slice(0, 10);

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");

const ZERO = { years: 0, months: 0, days: 0 };

/* Board 3071:2452 `icon/chev-up-16` — 9.7x5.7 of chevron in a 16 box, stroke
   #6a7282 at 1.7, round caps. It rotates to point down when collapsed, the
   same convention as .dx-card-chevron elsewhere in this design system. */
const CollapseChevron = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M3.15 10.15L8 5.3L12.85 10.15"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Approximate a Duration triple as a day count so Total interval and
// Started when can be compared. 30 days per month, 12 months per year —
// matches normalizeDuration's cascade convention so equivalent inputs
// (e.g. 1 year vs 12 months) compare equal.
const durationInDays = (d) =>
  ((d && d.years) || 0) * 360 +
  ((d && d.months) || 0) * 30 +
  ((d && d.days) || 0);

// Earliest date accepted by the Status update date picker. Guards against
// a stray keyboard entry (year "0055" or similar) that native input's max
// alone wouldn't catch on the low end.
const MIN_STATUS_UPDATE = "1900-01-01";

// Seed a Duration bag from an AI-supplied {years, months, days} object,
// or three flat fields (started_years / started_months / started_days),
// falling back to the current persisted value. Matches the two shapes
// the transcript service accepts on the wire.
const durationFromPayload = (payload, nestedKey, flatY, flatM, flatD, fallback) => {
  if (!payload) return fallback;
  if (payload[nestedKey] && typeof payload[nestedKey] === "object") {
    const raw = payload[nestedKey];
    return {
      years:  Number(raw.years)  || 0,
      months: Number(raw.months) || 0,
      days:   Number(raw.days)   || 0,
    };
  }
  const hasFlat =
    payload[flatY] !== undefined || payload[flatM] !== undefined || payload[flatD] !== undefined;
  if (hasFlat) {
    return {
      years:  Number(payload[flatY]) || 0,
      months: Number(payload[flatM]) || 0,
      days:   Number(payload[flatD]) || 0,
    };
  }
  return fallback;
};

const AddictionForm = ({
  patientId,
  formId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload,
  onSave,
  onReject,
  saving,
  error,
  themeClass,
  isReopen,
  menuClassName,
}) => {
  const addiction = editing || {};
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";

  // ZY-10356 Phase 1 — transcript-mode draft preservation. No-op in
  // standalone.
  const [draft, saveDraft] = useCardDraft(formId);

  const [status, setStatus] = useState(() => (
    draft && "status" in draft
      ? draft.status
      : ((p && p.status) || addiction.status || "never")
  ));
  const [statusUpdate, setStatusUpdate] = useState(() => {
    if (draft && "statusUpdate" in draft) return draft.statusUpdate;
    const seed =
      (p && (p.status_update || p.statusUpdate)) ||
      addiction.statusUpdate ||
      "";
    return toDateInput(seed) || todayISO();
  });
  const [startedWhen, setStartedWhen] = useState(() => (
    draft && "startedWhen" in draft
      ? draft.startedWhen
      : durationFromPayload(
          p,
          "started_when",
          "started_years",
          "started_months",
          "started_days",
          addiction.startedWhen || ZERO
        )
  ));
  const [totalInterval, setTotalInterval] = useState(() => (
    draft && "totalInterval" in draft
      ? draft.totalInterval
      : durationFromPayload(
          p,
          "total_interval",
          "total_years",
          "total_months",
          "total_days",
          addiction.totalInterval || ZERO
        )
  ));
  const [frequency, setFrequency] = useState(() => (
    draft && "frequency" in draft
      ? draft.frequency
      : ((p && p.frequency) || addiction.frequency || "")
  ));
  const [description, setDescription] = useState(() => (
    draft && "description" in draft
      ? draft.description
      : ((p && p.description) || addiction.description || "")
  ));
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change.
  useEffect(() => {
    saveDraft({
      status, statusUpdate, startedWhen, totalInterval, frequency, description,
    });
  }, [
    saveDraft,
    status, statusUpdate, startedWhen, totalInterval, frequency, description,
  ]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const isNever = status === "never";
  const isQuit = status === "quit";

  // Detail fields are only required when the status carries meaningful
  // details. Never = no detail fields at all. Any active status (regularly /
  // occasionally / quit) requires Frequency. Quit additionally requires Total
  // interval so "when did they stop" is captured explicitly. For regularly /
  // occasionally it stays optional — the table derives a live "till today"
  // value from Started when when the field is blank.
  const frequencyMissing = !isNever && !frequency.trim();
  const totalIntervalMissingForQuit = isQuit && isZeroDuration(totalInterval);
  // Total interval represents how long the addiction has been actively
  // used; Started when represents how long ago it began. Total > Started
  // is nonsensical (you can't have used it for longer than you've had it),
  // so we block Save on that inversion. Only enforced when the status
  // carries meaningful details (i.e. not "never") — a Never row leaves
  // both durations at zero and passes trivially.
  const totalIntervalExceedsStarted =
    !isNever &&
    !isZeroDuration(totalInterval) &&
    durationInDays(totalInterval) > durationInDays(startedWhen);
  const statusUpdateOutOfRange =
    !!statusUpdate &&
    (statusUpdate < MIN_STATUS_UPDATE || statusUpdate > todayISO());

  // Save-enable baseline — compare current form state against the
  // persisted record (`addiction.*`), NOT against the AI-seeded payload
  // (`p`). Why: an AI proposal lands with new fields (e.g. Started="6
  // months") on top of the DB row's prior values. The form seeds from
  // the merged view, so comparing against that merged view would always
  // report "no change" — the user couldn't accept the AI's own values.
  // Comparing against the DB record means:
  //   • AI emission that differs from DB → dirty from the start (reviewer
  //     can accept directly), matching the user's "6 months differs from
  //     saved 2 months → Update should enable" expectation.
  //   • No AI emission (updateCardsAdd row-click path) → form seeds from
  //     DB → baseline = DB → nothing dirty → button disabled, matching
  //     the earlier "no field touched since open → disabled" rule.
  //   • Reviewer edits on top → dirty, enabled.
  // Standalone mode: `p` null, `addiction` = the record under edit —
  // same behavior as before.
  const baseline = useMemo(() => ({
    status:        addiction.status || "never",
    statusUpdate:  toDateInput(addiction.statusUpdate) || todayISO(),
    frequency:     addiction.frequency || "",
    description:   addiction.description || "",
    startedWhen:   addiction.startedWhen || ZERO,
    totalInterval: addiction.totalInterval || ZERO,
  }), [addiction]);

  const isDirty = useMemo(() => {
    if (baseline.status !== status) return true;
    if (baseline.statusUpdate !== statusUpdate) return true;
    if (baseline.frequency !== frequency) return true;
    if (baseline.description.trim() !== description.trim()) return true;
    const sameDur = (a, b) =>
      (a || ZERO).years === b.years &&
      (a || ZERO).months === b.months &&
      (a || ZERO).days === b.days;
    if (!sameDur(baseline.startedWhen, startedWhen)) return true;
    if (!sameDur(baseline.totalInterval, totalInterval)) return true;
    return false;
  }, [baseline, status, statusUpdate, startedWhen, totalInterval, frequency, description]);

  // The rail's blank-sheet guard asks whether this sheet has anything on it.
  // Every sheet here is an update of an existing record, so it is never blank
  // in the "nothing typed yet" sense the guard is about.
  useEffect(() => {
    if (typeof onFilledChange !== "function") return;
    onFilledChange(formId, true);
  }, [formId, onFilledChange]);

  const busy = submitting || !!saving;
  // Save requires a real change from the opened state in BOTH modes.
  // The earlier `isTranscript || isDirty` escape hatch existed because
  // the dirty check used to compare against the DB record only, which
  // was always "wrong" for a transcript card seeded from AI payload.
  // With baseline now payload-first, isDirty is correct in both modes.
  const canSubmit =
    !busy &&
    !blockedBySibling &&
    !frequencyMissing &&
    !totalIntervalMissingForQuit &&
    !totalIntervalExceedsStarted &&
    !statusUpdateOutOfRange &&
    isDirty;

  const close = useCallback(() => onClose(formId), [onClose, formId]);

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;
    setFeedback(null);

    const body = {
      key: addiction.key,
      status,
      startedWhen: isNever ? ZERO : normalizeDuration(startedWhen),
      totalInterval: isNever ? ZERO : normalizeDuration(totalInterval),
      frequency: isNever ? "" : frequency.trim(),
      description: description.trim(),
      statusUpdate: statusUpdate ? toIsoUtc(statusUpdate) : undefined,
    };

    // Transcript mode — hand out; caller owns persistence. Skip the
    // local submitting/feedback + ADD_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }

    const svc = new AddictionService();
    const failed = (err) => {
      // Unmounted means the rail already dropped this sheet, and closeSheet
      // clears the saving flag on the way out — nothing to hand back.
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      setFeedback({ kind: "err", text: (err && err.message) || "Save failed — please retry" });
    };

    // Fresh version per mutation, never the one this sheet mounted with.
    // Sibling sheets are blocked from saving while this is in flight, so the
    // version read here cannot go stale between the read and the write.
    svc.list(
      patientId,
      (doc) => {
        svc.add(
          patientId,
          addiction.key,
          { ...body, expectedVersion: (doc && doc.version) || 0 },
          () => {
            EventService.emit(ADD_V2_REFRESH);
            if (!mounted.current) return;
            if (typeof onSavingChange === "function") {
              onSavingChange(formId, false);
            }
            // The rail drops this sheet, which unmounts us — anything set
            // after this point would be a setState on an unmounted component.
            close();
          },
          failed
        );
      },
      failed
    );
  };

  const band = addictionBand(status || "never");
  const statusId = `ad3-status-${formId}`;
  const dateId = `ad3-date-${formId}`;
  const freqId = `ad3-freq-${formId}`;
  const descId = `ad3-desc-${formId}`;

  const rootClass = [
    "ad3-dlg",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err" ? feedback.text : error || "";

  return (
    <section className={rootClass}>
      {/* ZY-10356 Phase 6 Rule 1 — top-right X close only on re-opens
          (#6/#8). First-opens (#5/#7) leave the footer Reject as the
          sole dismiss affordance. Addictions is a fixed-slot resource
          so #1-#4 never apply here. Mirrors CC / Alg / PA. */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={close}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* Board 3071:2447 — a bare 24px row, no divider beneath it. */}
      <header className="ad3-dlg-head">
        {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
        <span className="ad3-dlg-title">
          Update
          <span className="ad3-dlg-sub">&middot; {addiction.label}</span>
        </span>
        {/* The board draws a chevron-UP here, not an x — in this design system
            that is the card-collapse affordance, so it collapses rather than
            dismissing. Cancel in the footer is what closes the sheet. */}
        <button
          type="button"
          className="ad3-dlg-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${addiction.label}` : `Collapse ${addiction.label}`}
        >
          <CollapseChevron />
        </button>
      </header>

      <div className="ad3-dlg-fold">
        <div className="ad3-dlg-fold-inner">
          {/* The board's 7x7 leading dot is drawn un-tinted on its sample
              instance (a neutral #26284d against a "Regularly" label), so it
              is sample drift, not a spec — drive it from the band, as the
              table chip does. Select owns its own DOM, so the custom property
              rides on the field wrapper and the dot is a ::before on the
              trigger. */}
          <div className="ad3-fld ad3-status" style={{ "--ad3-dot": band.dot }}>
            <label className="ad3-lbl" htmlFor={statusId}>
              Status <span className="ad3-req">*</span>
            </label>
            <Select
              id={statusId}
              value={status}
              options={ADDICTION_STATUS}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              onChange={setStatus}
              menuClassName={menuClassName || ""}
            />
          </div>

          <div className="ad3-fld">
            <label className="ad3-lbl" htmlFor={dateId}>
              Status update date <span className="ad3-req">*</span>
            </label>
            <span className="ad3-date-wrap">
              <input
                id={dateId}
                className="ad3-input"
                type="date"
                value={statusUpdate}
                onChange={(e) => setStatusUpdate(e.target.value)}
                min={MIN_STATUS_UPDATE}
                max={todayISO()}
                disabled={busy}
              />
            </span>
            {statusUpdateOutOfRange && (
              <p className="fam-field-error">
                Status update date must be between 1900 and today.
              </p>
            )}
          </div>

          {/* Always rendered so the layout does not shift between statuses;
              disabled and greyed when status = never. */}
          <div className={`ad-detail-block${isNever ? " is-disabled" : ""}`}>
            <div className="ad3-fld">
              <label className="ad3-lbl">
                Started when <span className="ad3-lbl-note">&middot; how long ago</span>
              </label>
              {/* Same Y/M/D Stepper trio as Diagnosis's Status update date
                  (max 120 / 11 / 31). Each stepper auto-clamps to its max
                  during typing — e.g. "1111" in years → 120 — so no
                  out-of-range value can be entered. */}
              <div className="cc-duration" role="group" aria-label="Started when">
                <Stepper
                  label="Years"
                  value={startedWhen.years || 0}
                  onChange={(v) => setStartedWhen({ ...startedWhen, years: v || 0 })}
                  max={120}
                  disabled={isNever || busy}
                />
                <Stepper
                  label="Months"
                  value={startedWhen.months || 0}
                  onChange={(v) => setStartedWhen({ ...startedWhen, months: v || 0 })}
                  max={11}
                  disabled={isNever || busy}
                />
                <Stepper
                  label="Days"
                  value={startedWhen.days || 0}
                  onChange={(v) => setStartedWhen({ ...startedWhen, days: v || 0 })}
                  max={31}
                  disabled={isNever || busy}
                />
              </div>
            </div>

            <div className="ad3-fld">
              <label className="ad3-lbl">
                Total interval <span className="ad3-lbl-note">&middot; total time used</span>
                {isQuit && <span className="ad3-req"> *</span>}
              </label>
              <div className="cc-duration" role="group" aria-label="Total interval">
                <Stepper
                  label="Years"
                  value={totalInterval.years || 0}
                  onChange={(v) => setTotalInterval({ ...totalInterval, years: v || 0 })}
                  max={120}
                  disabled={isNever || busy}
                />
                <Stepper
                  label="Months"
                  value={totalInterval.months || 0}
                  onChange={(v) => setTotalInterval({ ...totalInterval, months: v || 0 })}
                  max={11}
                  disabled={isNever || busy}
                />
                <Stepper
                  label="Days"
                  value={totalInterval.days || 0}
                  onChange={(v) => setTotalInterval({ ...totalInterval, days: v || 0 })}
                  max={31}
                  disabled={isNever || busy}
                />
              </div>
              {totalIntervalMissingForQuit && (
                <p className="fam-field-error">
                  Total interval is required when status is Quit.
                </p>
              )}
              {totalIntervalExceedsStarted && (
                <p className="fam-field-error">
                  Total interval cannot exceed Started when.
                </p>
              )}
            </div>

            <div className="ad3-fld">
              <label className="ad3-lbl" htmlFor={freqId}>
                Frequency
                {!isNever && <span className="ad3-req"> *</span>}
              </label>
              <input
                id={freqId}
                className={`ad3-input${frequencyMissing ? " is-error" : ""}`}
                type="text"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                placeholder="e.g. 5 cigarettes per day"
                disabled={isNever || busy}
              />
              {frequencyMissing && <p className="fam-field-error">Frequency is required.</p>}
            </div>
          </div>

          <div className="ad3-fld">
            <label className="ad3-lbl" htmlFor={descId}>
              Description <span className="ad3-lbl-note">optional</span>
              <span className="ad3-lbl-max">
                {countWords(description)} / {MAX_DESCRIPTION_WORDS} words
              </span>
            </label>
            <textarea
              id={descId}
              className="ad3-textarea"
              value={description}
              onChange={(e) => setDescription(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))}
              placeholder="Add a short note about this update…"
              disabled={busy}
            />
          </div>

          {displayError && <div className="cc-feedback is-err">{displayError}</div>}

          {/* Board 3071:2475 — 40px tall, no divider above it, 10px gap. */}
          <div className="ad3-dlg-foot">
            <button
              type="button"
              className="ad3-btn ad3-btn-sec"
              onClick={() => {
                if (isTranscript && !busy && typeof onReject === "function") {
                  onReject();
                  return;
                }
                close();
              }}
            >
              {busy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
            </button>
            <button
              type="button"
              className={`ad3-btn ad3-btn-pri${themeClass ? ` ${themeClass}` : ""}`}
              onClick={submit}
              disabled={!canSubmit}
              title={
                blockedBySibling
                  ? "Another addiction is saving"
                  : !isDirty && !isTranscript
                  ? "No changes to save"
                  : undefined
              }
            >
              {/* Addictions is a 4-slot fixed resource — every card is
                  an update (#5-#8). Match CC's "Update" label in both
                  transcript and standalone for consistency. */}
              {busy
                ? (isTranscript ? "Updating…" : "Saving…")
                : isTranscript
                ? "Update"
                : "Save addiction"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

AddictionForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The addiction record this sheet updates. Never null — there is no add. */
  editing: PropTypes.object,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  onReject: PropTypes.func,
  /** ZY-10356 Phase 6 — surfaces the top-right X-close affordance on
   *  reopen cards (#6/#8). First-opens keep footer Reject only. */
  isReopen: PropTypes.bool,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /** Extra class for the Status dropdown menu portal. */
  menuClassName: PropTypes.string,
};

AddictionForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  isReopen: false,
  menuClassName: "",
};

export default AddictionForm;
